import { toastr } from 'react-redux-toastr';
import { BASE_PRINT_AGENT_URL } from '../../config/endpoints';
import { addQrCode, addText, createPdfDocument, setFont } from '../pdf';
import { DATE_TIME_FORMAT } from '../../config/locale';
import { tzNormalizeDate } from '../date';

const myX = 3;
let myY = 5;
const margin = 3;

const printLine = (doc, factor = 1) => {
  addText(
    doc,
    '----------------------------------------------------------------------------',
    40,
    (myY += margin * factor),
    80,
  );
};

const printQr = async (doc, { tagNumber }) => {
  const qrSize = 30;
  const pageWidth = 80;
  const qrX = (pageWidth - qrSize) / 2;
  await addQrCode(doc, String(tagNumber), qrX, myY, qrSize, qrSize);
  myY += qrSize + margin;
};

const printHeader = (doc) => {
  myY += margin * 2;

  setFont(doc, 'bold');
  doc.setFontSize(14);
  addText(doc, 'EQUIPAJE', 40, myY, 80);

  setFont(doc);
  doc.setFontSize(8);
  addText(
    doc,
    'Turismo Civa S.A.C - RUC:20102427891',
    40,
    (myY += margin * 1.5),
    75,
    'center',
  );

  printLine(doc);
};

const printOriginFields = (doc, { origin }) => {
  setFont(doc, 'bold');
  doc.setFontSize(9);
  addText(doc, 'ORIGEN:', myX, (myY += margin * 1.5), 75, 'left');
  setFont(doc);
  doc.setFontSize(10);
  addText(doc, origin.toUpperCase(), myX, (myY += margin + 2), 75, 'left');
  doc.setFontSize(8);
  printLine(doc);
};

const printTagFields = (doc, { tagNumber, createDate, ticketCode }) => {
  setFont(doc);
  doc.setFontSize(15);
  setFont(doc, 'bold');
  addText(
    doc,
    `Nº: ${tagNumber ? String(tagNumber).padStart(7, '0') : '-'}`,
    myX,
    (myY += margin * 1.5),
    75,
    'left',
  );
  setFont(doc);
  doc.setFontSize(10);
  addText(
    doc,
    `Fecha: ${tzNormalizeDate({ date: createDate, format: DATE_TIME_FORMAT })}`,
    myX,
    (myY += margin + 2),
    75,
    'left',
  );
  addText(
    doc,
    `Boleto: ${ticketCode || '-'}`,
    myX,
    (myY += margin + 2),
    75,
    'left',
  );
  doc.setFontSize(8);
  printLine(doc);
};

const lineStep = (doc, lines) =>
  doc.getTextDimensions(lines, {
    fontSize: doc.getFontSize(),
    lineHeightFactor: 1.1,
  }).h / lines.length;

const printPassengerFields = (
  doc,
  { fullName, documentType, documentNumber },
) => {
  setFont(doc, 'bold');
  doc.setFontSize(9);
  addText(doc, 'PASAJERO:', myX, (myY += margin * 1.5), 75, 'left');
  setFont(doc);
  doc.setFontSize(10);
  addText(doc, fullName, myX, (myY += margin + 2), 75, 'left');
  addText(
    doc,
    `${documentType}: ${documentNumber}`,
    myX,
    (myY += margin + 2),
    75,
    'left',
  );
  doc.setFontSize(8);
  printLine(doc);
};

const printDestinationFields = (doc, { destination }) => {
  setFont(doc, 'bold');
  doc.setFontSize(9);
  addText(doc, 'DESTINO:', myX, (myY += margin * 1.5), 75, 'left');
  doc.setFontSize(11);
  addText(doc, destination.toUpperCase(), myX, (myY += margin + 2), 75, 'left');
  doc.setFontSize(8);
  setFont(doc);
  printLine(doc);
};

const printDetailFields = (doc, { totalWeight, totalItems, itemList }) => {
  setFont(doc, 'bold');
  doc.setFontSize(9);
  addText(doc, 'DETALLE:', myX, (myY += margin * 1.5), 75, 'left');
  setFont(doc);
  doc.setFontSize(10);
  addText(doc, `Peso: ${totalWeight} KG`, myX, (myY += margin + 2), 75, 'left');
  addText(doc, `Items: ${totalItems}`, myX, (myY += margin + 2), 75, 'left');

  addText(doc, 'EQUIPAJES:', myX, (myY += margin + 2), 75, 'left');

  const itemCategories = itemList
    .map((item) => item.itemCategoryName)
    .join(', ');
  const itemDescriptions = itemList.map((item) => item.description).join(', ');

  const typeLines = doc.splitTextToSize(`Tipo: ${itemCategories}`, 75);
  const descLines = doc.splitTextToSize(`Descripción: ${itemDescriptions}`, 75);

  myY += margin + 2;
  addText(doc, typeLines, myX, myY, 75, 'left');
  myY += (typeLines.length - 1) * lineStep(doc, typeLines);

  myY += margin + 2;
  addText(doc, descLines, myX, myY, 75, 'left');
  myY += (descLines.length - 1) * lineStep(doc, descLines);

  myY += margin * 2.5;
};

const formatBaggage = (baggage) => ({
  tagNumber: baggage.tagNumber,
  createDate: baggage.createDate,
  fullName: baggage.mainTicket.customer.fullName,
  documentType: baggage.mainTicket.customer.identificationType.name,
  documentNumber: baggage.mainTicket.customer.idDocumentNumber,
  destination: baggage.destinationLocation.name,
  totalWeight: baggage.totalWeight,
  totalItems: baggage.itemList.length,
  origin: baggage.sourceLocation.name,
  itemList: baggage.itemList || [],
  ticketCode: baggage.mainTicket.ticketCode,
});

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export default async function printBaggageTag(baggage) {
  const doc = createPdfDocument('p', 'mm', [80, 160]);

  const {
    tagNumber,
    createDate,
    fullName,
    documentType,
    documentNumber,
    destination,
    totalWeight,
    totalItems,
    origin,
    itemList,
    ticketCode,
  } = formatBaggage(baggage);

  myY = 5;
  printHeader(doc);
  printTagFields(doc, { tagNumber, createDate, ticketCode });
  printPassengerFields(doc, { fullName, documentType, documentNumber });
  printOriginFields(doc, { origin });
  printDestinationFields(doc, { destination });
  printDetailFields(doc, { totalWeight, totalItems, itemList });
  await printQr(doc, { tagNumber });

  doc.autoPrint();
  const arrayBuffer = doc.output('arraybuffer');

  const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(blob);
  const pdfBase64 = arrayBufferToBase64(arrayBuffer);

  try {
    await fetch(`${BASE_PRINT_AGENT_URL}/health`);
  } catch {
    window.open(blobUrl, '_blank');
    toastr.error(
      'Error',
      'El agente de impresión no está corriendo. Abre print-agent.exe primero.',
    );
    return;
  }

  try {
    const response = await fetch(`${BASE_PRINT_AGENT_URL}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pdfBase64, printingType: 'voucher' }),
    });

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.error || 'Error al imprimir');
    }
  } catch (err) {
    window.open(blobUrl, '_blank');
    toastr.error('Error', err.message);
  }
}
